import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import getLPCRegionLOS from 'lib/offer/getLPCRegionLOS'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import { useAppSelector } from 'hooks/reduxHooks'
import { useOfferPackagePrice } from 'hooks/Offers/useOfferPriceNew'
import { getDefaultAirportCode } from 'selectors/flightsSelectors'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import getOfferDurationText from 'lib/offer/getOfferDurationText'
import useOffersBestPrices from 'hooks/useOfferBestPrices'
import { EmptyObject } from 'lib/object/objectUtils'
import { isFlashHotelOffer, isHotelPackage } from 'lib/offer/offerTypes'
import { useMaldivesDirectBundleContext } from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectBundleContext'
import { useDirectSearchPrices } from 'hooks/Search/useSearchPrices'
import { useCharterFlightPrice } from 'hooks/useFlightPrice'
import { getCharterBundleRoute } from 'selectors/offerPage/initialOfferPageStateSelectors'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { FlightsClassTypes } from 'constants/flight'
import { MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG, MALDIVES_CHARTER_END_TARGET_DATE, MALDIVES_CHARTER_START_TARGET_DATE } from 'constants/charterFlight'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { resolveAllowedFareClass } from 'lib/charterFlights/charterOfferFareClassRestrictions'

interface Props {
  offer: App.HotelOffer | App.HotelOfferSummary | App.TourOffer | App.TourOfferSummary | App.VillaOffer | App.VillaOfferSummary
  selectedDatesAndRoomsFilter?: App.OfferListFilters
}

const defaultOccupancy: Array<App.Occupants> = [{ adults: 2 }]

function LEOfferForceBundlePricing(props: Props) {
  const {
    offer,
    selectedDatesAndRoomsFilter = EmptyObject,
  } = props
  const intl = useIntl()

  const bestPkg = useOffersBestPrices(offer, selectedDatesAndRoomsFilter)
  const pkg = bestPkg?.bestPricePackage ?? offer.lowestPricePackage!

  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)

  // Maldives charter bundle mode (homepage carousel)
  const { charterBundle: charterBundleFromContext, isPricePerPerson, fareClass } = useMaldivesDirectBundleContext()
  const rooms = selectedDatesAndRoomsFilter.rooms ?? defaultOccupancy
  const totalPassengers = useMemo(() => sumUpOccupancies(rooms), [rooms])
  const charterBundleRoute = useAppSelector(state => getCharterBundleRoute(state, offer))
  const selectedFareClass = fareClass ?? FlightsClassTypes.ECONOMY
  const effectiveFareClass = useMemo(() => {
    const resolvedFareClass = resolveAllowedFareClass({
      selectedFareClass,
      campaignTags: offer.campaignTags,
      occupancies: rooms,
    })
    return resolvedFareClass ?? selectedFareClass
  }, [selectedFareClass, offer.campaignTags, rooms])

  // Auto-detect charter bundle mode: if offer has charter campaign tag, treat as charter bundle
  // This ensures charter offers always show per-person pricing even without context wrapper
  const isCharterBundleOffer = useMemo(() => {
    return isFlashHotelOffer(offer) &&
      offer.offerFlightsEnabled &&
      offer.campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)
  }, [offer])
  const charterBundle = charterBundleFromContext || isCharterBundleOffer

  const shouldFetchCharterFlightPrice = !!(
    charterBundle &&
    charterBundleRoute &&
    rooms.length
  )

  const [charterFlightPrice] = useCharterFlightPrice({
    charterBundleRoute: charterBundleRoute ?? 'melbourne-maldives',
    checkInDate: selectedDatesAndRoomsFilter.checkIn ?? MALDIVES_CHARTER_START_TARGET_DATE,
    checkOutDate: selectedDatesAndRoomsFilter.checkOut ?? MALDIVES_CHARTER_END_TARGET_DATE,
    occupancies: rooms,
    fareClass: effectiveFareClass,
    disabled: !shouldFetchCharterFlightPrice,
  })

  const directSearchPrices = useDirectSearchPrices({ filters: selectedDatesAndRoomsFilter, offerId: offer.id })

  const bundleComputed = useMemo(() => {
    if (!charterBundle) return undefined
    if (!charterBundleRoute) return undefined

    // Prefer direct search pricing; fallback to package pricing.
    const roomMultiplier = rooms.length || 1
    const hotelTotalPrice = directSearchPrices?.lowestPrice ?? ((pkg.price ?? 0) * roomMultiplier)
    const hotelTotalMemberPrice = directSearchPrices?.lowestMemberPrice ?? ((pkg.memberPrice ?? 0) * roomMultiplier)
    const pkgValueOrMemberValue = canViewLuxPlusBenefits ? (pkg.memberValue ?? pkg.value ?? 0) : (pkg.value ?? 0)
    const hotelTotalValue = directSearchPrices?.lowestPriceValue ?? (pkgValueOrMemberValue * roomMultiplier)

    const flightTotalPrice = charterFlightPrice ?? 0
    const totalPrice = hotelTotalPrice + flightTotalPrice
    const totalMemberPrice = hotelTotalMemberPrice > 0 ? (hotelTotalMemberPrice + flightTotalPrice) : undefined
    const totalValue = hotelTotalValue + flightTotalPrice

    const divisor = isPricePerPerson && totalPassengers > 0 ? totalPassengers : 1
    const price = totalPrice / divisor
    const memberPrice = totalMemberPrice !== undefined ? (totalMemberPrice / divisor) : undefined
    const value = totalValue / divisor

    const discountPercent = getValueOffPercent(value, price)
    const memberDiscountPercent = memberPrice !== undefined ? getValueOffPercent(value, memberPrice) : undefined

    return {
      price,
      memberPrice,
      value,
      discountPercent,
      memberDiscountPercent,
      saleUnit: isPricePerPerson ? 'per person' : 'total',
    }
  }, [
    canViewLuxPlusBenefits,
    charterBundle,
    charterBundleRoute,
    charterFlightPrice,
    directSearchPrices?.lowestMemberPrice,
    directSearchPrices?.lowestPrice,
    directSearchPrices?.lowestPriceValue,
    isPricePerPerson,
    pkg.memberPrice,
    pkg.memberValue,
    pkg.price,
    pkg.value,
    rooms.length,
    totalPassengers,
  ])

  const flightOrigin = useAppSelector(state => getDefaultAirportCode(state))
  const flightPrice = offer.flightPrices[flightOrigin || '']
  const {
    withFlights: pricingWithFlights,
  } = useOfferPackagePrice(pkg, offer, { flightPrice })

  if (!bundleComputed && !pricingWithFlights) {
    return null
  }

  const totalPriceWithFlights = bundleComputed?.price ?? pricingWithFlights!.price
  const memberPriceWithFlights = bundleComputed?.memberPrice
  const displayTotalValue = bundleComputed?.value ?? pricingWithFlights!.value
  const discountPercent = bundleComputed?.discountPercent ?? pricingWithFlights!.discountPercent
  const valueDiscountPercent = (canViewLuxPlusBenefits && bundleComputed?.memberDiscountPercent !== undefined)
    ? bundleComputed.memberDiscountPercent
    : discountPercent

  const shouldShowRate = !offer.isDiscountPillHidden && discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  const shouldShowValue = pkg.value > 0 && totalPriceWithFlights && displayTotalValue > totalPriceWithFlights && pkg.shouldDisplayValue &&
    (isFlashHotelOffer(offer) || (isHotelPackage(pkg) && !pkg.roomRate?.inclusionsHideValue))

  let staysFrom = offer.minDuration
  const LPCRegionLOS = getLPCRegionLOS(offer)
  if (LPCRegionLOS && offer.packages.some(p => p.duration === LPCRegionLOS)) {
    staysFrom = LPCRegionLOS
  }
  const showStaysFromLabel = staysFrom && offer.type === OFFER_TYPE_ALWAYS_ON && LPCRegionLOS && staysFrom < pkg.duration

  const durationText = bundleComputed
    ? intl.formatMessage(
        {
          id: 'gEFgViLaxzHaUoh9q6IV',
          defaultMessage: '{count, plural, one {# night} other {# nights}} & flights from',
          description: 'Bundle offer duration in nights with flights, shown as price caption on offer card',
        },
        { count: pkg.duration },
      )
    : getOfferDurationText(intl, offer, pkg)

  const priceElement = bundleComputed ? (
    <LuxPlusPriceStack
      price={totalPriceWithFlights}
      memberPrice={memberPriceWithFlights}
      size="L"
      saleUnit={bundleComputed.saleUnit}
      offerConfig={offer.luxPlus}
      testid="main-offer-pricing"
    />
  ) : (
    <PriceRowPrice
      price={totalPriceWithFlights}
      saleUnit={pricingWithFlights!.saleUnit}
      size="L"
    />
  )

  return (<>
    <PriceRowPriceCaption>{durationText}</PriceRowPriceCaption>
    {priceElement}
    {shouldShowValue && <PriceRowValueDiscountWithCaption
      size="L"
      originalValue={Math.ceil(displayTotalValue)}
      discountPercentage={shouldShowRate ? valueDiscountPercent : undefined}
    />}
    {staysFrom && showStaysFromLabel && <PriceRowCaption>
      <FormattedMessage
        id="7PFPr3tSopW8Hgmmb_Dy"
        defaultMessage="Stays from {staysFrom, plural, one {# night} other {# nights}} available"
        description="Caption indicating minimum stay duration available for an always-on offer"
        values={{ staysFrom }}
      />
    </PriceRowCaption>}
    {!!pkg.roomOccupancy && offer.type === 'tour' && <PriceRowCaption>
      {pkg.roomOccupancy}
    </PriceRowCaption>}
  </>)
}

export default LEOfferForceBundlePricing
